// src/components/management/Steps.jsx



import React, { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import StepNavigator from './steps/StepNavigator';
import TaskList      from './steps/TaskList';
import StepForm      from './steps/StepForm';
import TaskForm      from './steps/TaskForm';
import '../../styles/components/management/steps/Steps.css';

const API_BASE = process.env.REACT_APP_API_BASE;

export default function Steps() {
  const { user } = useAuth();
  const [units, setUnits]               = useState([]);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [activeStep, setActiveStep]     = useState(null);
  const [editingStep, setEditingStep]   = useState(null);
  const [showStepForm, setShowStepForm] = useState(false);
  const [editingTask, setEditingTask]   = useState(null);
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [taskReload, setTaskReload]     = useState(0);

  // —————— Audit logger ——————
 const logAudit = useCallback(async (action, details) => {
  await fetch(`${API_BASE}/app/API/rbmgt/AuditLogs.php`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      user_id: user?.id || 0,
      user_name: `${user?.name || "System"} ${user?.surname || ""}`.trim(),
      action,
      details,
    }),
  });
}, [user]);

  // —————— Fetch all units & log it ——————
  useEffect(() => {
  const fetchUnits = async () => {
    try {
      const res = await fetch(`${API_BASE}/app/API/rbmgt/unit.php`);
      const data = await res.json();
      if (data.success && Array.isArray(data.units)) {
        setUnits(data.units);
      }

      await logAudit("Fetch Units", "Loaded unit list for steps panel");
    } catch (err) {
      console.error("Fetch units error:", err);
    }
  };
  fetchUnits();
}, [logAudit]); // ✅ Include logAudit


  // Reset step when unit changes
  useEffect(() => {
    setActiveStep(null);
  }, [selectedUnit]);

  // —————— DELETE a step link ——————
  const deleteUnitStep = async (unitId, stepId) => {
    if (!window.confirm("Delete this step?")) return;
    await fetch(
      `${API_BASE}/app/API/rbmgt/unit_step.php` +
      `?unit_id=${unitId}&step_template_id=${stepId}`,
      { method: "DELETE" }
    );
    await logAudit(
      "Delete Unit-Step Link",
      `Unit ${unitId}, Step ${stepId}`
    );
    setActiveStep(null);
  };

  // —————— DELETE a task link ——————
  const deleteUnitStepTask = async (unitId, stepId, taskId) => {
    if (!window.confirm("Delete this task?")) return;
    await fetch(
      `${API_BASE}/app/API/rbmgt/unit_step_task.php` +
      `?unit_id=${unitId}&step_template_id=${stepId}&task_template_id=${taskId}`,
      { method: "DELETE" }
    );
    await logAudit(
      "Delete Unit-Step-Task Link",
      `Unit ${unitId}, Step ${stepId}, Task ${taskId}`
    );
    setTaskReload(c => c + 1);
  };

  return (
    <div className="steps-page">
      {/* ————— Unit Selector ————— */}
      <div className="unit-selector">
        <label htmlFor="unit-select">Select Unit:</label>
        <select
          id="unit-select"
          value={selectedUnit?.UNIT_ID || ""}
          onChange={e =>
            setSelectedUnit(
              units.find(u => u.UNIT_ID === e.target.value) || null
            )
          }
        >
          <option value="">-- choose a unit --</option>
          {units.map(u => (
            <option key={u.UNIT_ID} value={u.UNIT_ID}>
              {u.PROPERTY_NAME} — {u.UNIT_NAME}
            </option>
          ))}
        </select>
      </div>

      {selectedUnit && (
        <div className="steps-container">
          {/* ————— Steps Navigator ————— */}
          <StepNavigator
            unitId={selectedUnit.UNIT_ID}
            activeStep={activeStep}
            onSelectStep={setActiveStep}
            onAddStep={() => {
              setEditingStep({ unitId: selectedUnit.UNIT_ID });
              setShowStepForm(true);
            }}
            onEditStep={step => {
              setEditingStep(step);
              setShowStepForm(true);
            }}
            onDeleteStep={stepId =>
              deleteUnitStep(selectedUnit.UNIT_ID, stepId)
            }
          />

          {showStepForm && (
            <StepForm
              unitId={selectedUnit.UNIT_ID}
              initial={editingStep}
              onClose={() => setShowStepForm(false)}
              onSaved={async (isNew, templateId, order) => {
                // isNew: true=create, false=edit; templateId and order from StepForm
                await logAudit(
                  isNew ? "Create Step" : "Edit Step",
                  `Step ${templateId} @ Unit ${selectedUnit.UNIT_ID}, order ${order}`
                );
                setShowStepForm(false);
                setActiveStep(null);
              }}
            />
          )}

          {/* ————— Tasks ————— */}
          {activeStep && (
            <>
              <TaskList
                key={`${selectedUnit.UNIT_ID}-${activeStep}-${taskReload}`}
                unitId={selectedUnit.UNIT_ID}
                stepId={activeStep}
                reloadTrigger={taskReload}
                onAddTask={() => {
                  setEditingTask({
                    unitId: selectedUnit.UNIT_ID,
                    stepId: activeStep,
                  });
                  setShowTaskForm(true);
                }}
                onEditTask={task => {
                  setEditingTask(task);
                  setShowTaskForm(true);
                }}
                onDeleteTask={taskId =>
                  deleteUnitStepTask(
                    selectedUnit.UNIT_ID,
                    activeStep,
                    taskId
                  )
                }
              />

              {showTaskForm && (
                <TaskForm
                  unitId={selectedUnit.UNIT_ID}
                  stepId={activeStep}
                  initial={editingTask}
                  onClose={() => setShowTaskForm(false)}
                  onSaved={async (
                    isNew,
                    templateId /*, other details if you return them */
                  ) => {
                    await logAudit(
                      isNew ? "Create Task" : "Edit Task",
                      `Task ${templateId} @ Unit ${selectedUnit.UNIT_ID}, Step ${activeStep}`
                    );
                    setShowTaskForm(false);
                    setTaskReload(c => c + 1);
                  }}
                />
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
